import React, { useState, useRef, useEffect } from "react";
import { View, Text, TextInput, StyleSheet, Modal, Image, TouchableOpacity } from "react-native";
import { useTranslation } from 'react-i18next';
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import Colors from "../../../../../constants/colors";
import { ComponentStyles } from "../../../../../constants/componentStyles";

type PinInputStepProps = {
  handleForgotPassword?: () => void;
  attemptsLeft?: number;
  locked?: boolean;
  resetSignal?: number; 
};

export default function PinInputStep({ attemptsLeft, locked, handleForgotPassword, resetSignal = 0 }: PinInputStepProps) {
  const { t } = useTranslation();
  const [pin, setPin] = useState(["", "", "", ""]);
  const inputs = useRef<(TextInput | null)[]>([]);
  const setConsumerPin = useSessionStore((s: any) => s.setConsumerPin);
  const isNewConsumer = useSessionStore((s: SessionState) => (s as any).isNewConsumer);

  // 🔑 watch for resetSignal changes
  useEffect(() => {
    setPin(["", "", "", ""]);
    inputs.current[0]?.focus();
  }, [resetSignal]);



  const handleChange = (text: string, index: number) => {
    if (/^\d*$/.test(text)) {
      const newPin = [...pin];
      newPin[index] = text;
      setPin(newPin);
      try { setConsumerPin?.(newPin.join("")); } catch {}

      // Move to next box automatically
      if (text && index < 3) {
        inputs.current[index + 1]?.focus();
      }

      // Backspace handling
      if (!text && index > 0) {
        inputs.current[index - 1]?.focus();
      }
    }
  };

  return (
    <View style={styles.container}>
      <Text style={styles.title}>{t("PIN_INPUT_TITLE")}</Text>
      <Text style={styles.subtitle}>{t("PIN_INPUT_SUBTITLE")}</Text>

      <View style={styles.pinContainer}>
        {pin.map((digit, index) => (
          <TextInput
            key={index}
            testID={`sendPinInput${index}`}
            ref={(el) => (inputs.current[index] = el)}
            style={styles.pinInput}
            keyboardType="number-pad"
            maxLength={1}
            secureTextEntry
            value={digit}
            onChangeText={(text) => handleChange(text, index)}
          />
        ))}
      </View>

      {isNewConsumer ?
        pin.join("").length > 0 && pin.join("").length < 4 && (
        <Text style={styles.error}>{t("PIN_INPUT_ERROR")}</Text>
      )
        : !locked ? (
        <Text style={styles.attempts}>
          {attemptsLeft} {attemptsLeft === 1 ? "attempt" : "attempts"} remaining
        </Text>
      ) : (
        <Text style={styles.locked}>{t("ACCOUNT_LOCKED")}</Text>
      )}
      {/* {pin.join("").length > 0 && pin.join("").length < 4 && (
        <Text style={styles.error}>{t("PIN_INPUT_ERROR")}</Text>
      )} */}
      {/* {!locked ? (
        <Text style={styles.attempts}>
          {attemptsLeft} {attemptsLeft === 1 ? "attempt" : "attempts"} remaining
        </Text>
      ) : (
        <Text style={styles.locked}>{t("ACCOUNT_LOCKED")}</Text>
      )} */}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 24,
    backgroundColor: Colors.white
  },
  title: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.titleFont,
    fontWeight: '500', 
    color: Colors.titleTextColor,
    lineHeight: 34,
    marginBottom: 8
  },
  subtitle: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: '400',
    lineHeight: 24,
    color: Colors.subTitleColor,
    marginBottom: 32
  },
  pinContainer: {
    flexDirection: "row",
    marginBottom: 87,
  },
  pinInput: {
    width: 56,
    height: 56,
    borderRadius: 8,
    textAlign: "center",
    fontSize: 22,
    fontWeight: "600",
    backgroundColor: Colors.searchCompanyBackground,
    marginRight: 10
  },
  error: {
    color: "red",
    fontSize: 12,
    marginTop: 8,
  },
  attempts: {
    fontSize: 14,
    color: "#6B7280" },
  locked: {
    fontSize: 14,
    color: "red",
    fontWeight: "700"
  },
  modalOverlay: {
    flex: 1,
    justifyContent: "flex-end",
    backgroundColor: "rgba(0,0,0,0.4)"
  },
  modalContent: {
    backgroundColor: "white", padding: 20,
    borderTopLeftRadius: 20, borderTopRightRadius: 20,
  },
  accountLock: {
    fontSize: 24,
    fontWeight: "500",
    lineHeight: 28,
    fontFamily: 'Rubik',
    marginVertical: 26,
    color: Colors.accountLock,
  },
  accountLockInfo: {
    fontSize: 14,
    fontWeight: "400",
    lineHeight: 18,
    fontFamily: 'Rubik',
    marginBottom: 48,
    color: Colors.accountLock,
  },
  button: {
    backgroundColor: Colors.titleTextColor,
    paddingVertical: 16,
    borderRadius: 8,
    alignItems: "center",
  },
  buttonText: {
    color: Colors.white,
    fontSize: 16,
    fontWeight: "500",
    fontFamily: 'Rubik',
    lineHeight: 20,
    paddingHorizontal: 100
  },
});
